<template>
  <div class="requests">
    <new-main-header
        v-if="hasNewLeftNavEnabled"
        :show-plc-button="!isFraud"
        :show-one-off-button="!isFraud">
      Customer Requests
    </new-main-header>
    <main-header
        v-else
        :show-plc-button="true && !isFraud"
        :show-one-off-button="true && !isFraud">
      <span slot="arrow-back">
        <el-tooltip
            class="item"
            effect="dark"
            content="Back to Start Page"
            placement="bottom-start">
          <router-link :to="{ name: 'HomePage' }"><i class="el-icon-back" /> </router-link>
        </el-tooltip>
      </span>
      <span slot="page-title">Customer Requests</span>
    </main-header>
    <main-content>
      <div slot="main-content">
        <div style="margin: 10px 0px; text-align: right;">
          <el-popover
              v-model="visible"
              placement="left"
              title="Search and Filter"
              width="400"
              trigger="click">
            <el-form>
              <el-form-item label="UUID">
                <el-input
                    v-model="q.uuid"
                    placeholder="Search by UUID" />
              </el-form-item>
              <el-form-item label="GID">
                <el-input
                    v-model="q.gid"
                    placeholder="Search by GID" />
              </el-form-item>
              <el-form-item label="Device ID">
                <el-input
                    v-model="q.device_id"
                    placeholder="Search by Mac Address" />
              </el-form-item>
              <el-form-item label="State">
                <el-select
                    v-model="q.state"
                    style="width: 100%;"
                    filterable
                    placeholder="Search by state">
                  <el-option
                      v-for="state in statusList"
                      :key="state.value"
                      :value="state.value"
                      :label="state.label" />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button
                    type="info"
                    style="width: 100%; padding: 10px;"
                    @click="makeSearch()">
                  Search
                </el-button>
              </el-form-item>
            </el-form>

            <el-button-group slot="reference">
              <el-button
                  type="info"
                  style="padding: 11px 20px 10px;"
                  round>
                {{ $t('requests.search') }}
              </el-button>
              <el-button
                  v-if="isFiltered"
                  type="info"
                  style="padding: 10px 20px;"
                  round
                  icon="el-icon-close"
                  @click="clearParams()" />
            </el-button-group>
          </el-popover>
          <div
              v-if="isFiltered"
              style="margin-top: 10px">
            <el-tag
                v-for="(filter, i) in currentFilters"
                :key="i"
                style="margin-right: 3px"
                type="info"
                size="medium">
              {{ filter }}
            </el-tag>
          </div>
        </div>
        <el-tabs
            v-model="activeName"
            class="ring-tabs-nav">
          <el-tab-pane name="replacement">
            <span slot="label">
              <el-badge
                  :value="replacementSize"
                  :hidden="replacementSize === 0"
                  type="primary"
                  class="item">
                <span>Replacements</span>
              </el-badge>
            </span>
            <replacement-list
                :loading="loading"
                :requests="requestList.replacement"
                :status-list="statusList"
                @reload="loadRequests"
                @cancel-request="(requestId) => cancelRequest(requestId)" />
          </el-tab-pane>
          <el-tab-pane name="one_off">
            <span slot="label">
              <el-badge
                  :value="oneOffsSize"
                  :hidden="oneOffsSize === 0"
                  type="primary"
                  class="item">
                <span>One Off</span>
              </el-badge>
            </span>
            <one-off-list
                :loading="loading"
                :requests="requestList.one_off"
                :status-list="statusList"
                @reload="loadRequests" />
          </el-tab-pane>
          <el-tab-pane name="parts">
            <span slot="label">
              <el-badge
                  :value="partsSize"
                  :hidden="partsSize === 0"
                  type="primary"
                  class="item">
                <span>Parts</span>
              </el-badge>
            </span>
            <parts-list
                :loading="loading"
                :requests="requestList.parts"
                :status-list="statusList"
                @reload="loadRequests" />
          </el-tab-pane>
          <el-tab-pane name="refund">
            <span slot="label">
              <el-badge
                  :value="refundsSize"
                  :hidden="refundsSize === 0"
                  type="primary"
                  class="item">
                <span>Refunds</span>
              </el-badge>
            </span>
            <refund-list
                :loading="loading"
                :requests="requestList.refund"
                :status-list="statusList"
                @reload="loadRequests" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </main-content>
  </div>
</template>

<script>
import MainContent from "@/containers/Core/MainContent";
import MainHeader from "@/containers/Core/Header/MainHeader";
import ReplacementList from "@/containers/Customer/Requests/ReplacementList";
import RefundList from "@/containers/Customer/Requests/RefundList";
import PartsList from "@/containers/Customer/Requests/PartsList";
import OneOffList from "@/containers/Customer/Requests/OneOffList";
import CustomerRequestLogs from "@/containers/Customer/Requests/CustomerRequestLogs";
import NewMainHeader from "@/NewUI/containers/core/header/MainHeader";

import { compact, get, isEmpty, keys, map } from "lodash";
import { createNamespacedHelpers as h } from "vuex";
import permission from "@/mixins/permission";
import axios from "axios";

const { mapGetters: accountGetters } = h("db/account");
const { mapGetters: requestGetters } = h("db/requests");
const { mapActions: requestActions } = h("db/requests");

export default {
  name: "Requests",
  components: {
    MainContent,
    MainHeader,
    ReplacementList,
    RefundList,
    PartsList,
    OneOffList,
    CustomerRequestLogs,
    NewMainHeader,
  },
  mixins: [permission],
  data() {
    return {
      loading: false,
      requestList: {
        replacement: [],
        one_off: [],
        parts: [],
        refund: [],
      },
      activeName: "replacement",
      visible: false,
      statusList: [
        { label: "Pending", value: "pending" },
        { label: "Pending Stock", value: "pending_stock" },
        { label: "Processing", value: "in_process" },
        { label: "Manual Tracking", value: "manual_tracking" },
        { label: "Label Created", value: "label_created" },
        { label: "Waiting Customer", value: "waiting_for_updates" },
        { label: "Shipped", value: "shipped" },
        { label: "Resolved", value: "resolved" },
        { label: "Failed", value: "failed" },
        { label: "Paid", value: "paid" },
        { label: "Canceled", value: "canceled" },
        { label: "On Hold", value: "on_hold" },
        { label: "Missing Price", value: "internal_on_hold" },
        { label: "Missing Sku (Shopify)", value: "external_on_hold" },
        { label: "Return Label Created", value: "return_label_created" },
        { label: "Ready for Processing", value: "ready_for_processing" },
        { label: "Ship on return", value: "ship_on_return" },
      ],
      isFiltered: false,
      q: {
        uuid: "",
        gid: "",
        device_id: "",
        state: "",
      },
    };
  },
  computed: {
    ...accountGetters(["customer"]),
    isFraud() {
      return get(this.customer, "profile.fraud", false);
    },
    currentFilters() {
      return keys(this.q)
        .filter((key) => this.q[key].trim() !== "")
        .map((key) => {
          if (key == "state")
            return `${key}: ${this.statusDescription(this.q[key])}`;
          else return `${key}: ${this.q[key]}`;
        });
    },
    replacementSize() {
      return this.requestList.replacement.length;
    },
    oneOffsSize() {
      return this.requestList.one_off.length;
    },
    partsSize() {
      return this.requestList.parts.length;
    },
    refundsSize() {
      return this.requestList.refund.length;
    },
    hasNewLeftNavEnabled() {
      return this.hasPermission("NewUI", "leftNav");
    },
  },
  mounted() {
    if (this.$route?.query && !isEmpty(this.$route.query)) {
      let { "plc-uuid": uuid, "plc-gid": gid } = this.$route.query;
      this.q.uuid = uuid || "";
      this.q.gid = gid || "";
      this.makeSearch();
    } else {
      this.loadRequests();
    }
  },
  methods: {
    setTab(name) {
      this.activeName = name;
    },
    statusDescription(status) {
      const items = this.statusList.filter((item) => item.value == status);
      if (items.length == 0) return status;
      return items[0].label;
    },
    loadRequests() {
      this.loading = true;
      this.fetchAccountRequests(this.customer.id, undefined)
        .then((response) => {
          this.requestList = response.requests;
        })
        .catch((error) => console.info("Error loading account requests", error))
        .finally(() => (this.loading = false));
    },
    uncollapsed(requestType, uuid) {
      this.setTab(requestType);
    },
    getRequestTypeIfThereIsOnlyOneTypeOfRequests(requests) {
      const nonEmptyRequestTypes = Object.keys(requests).filter(
        (reqType) =>
          Array.isArray(requests[reqType]) && requests[reqType].length > 0
      );

      const count = nonEmptyRequestTypes.length;
      return count === 1 ? nonEmptyRequestTypes[0] : null;
    },
    makeSearch() {
      this.visible = false;
      let params = map(this.q, (value, key) => {
        if (value) return `${key}=${encodeURIComponent(value)}`;
        else return null;
      });
      this.fetchAccountRequests(this.customer.id, compact(params))
        .then((response) => {
          this.isFiltered = true;
          let requests = response.requests;
          this.requestList = response.requests;
          const requestType =
            this.getRequestTypeIfThereIsOnlyOneTypeOfRequests(requests);
          if (requestType) {
            const req = requests[requestType][0];
            this.$nextTick(() => this.uncollapsed(requestType, req.uuid));
          }
        })
        .catch((error) => console.info("Error loading account requests", error))
        .finally(() => (this.loading = false));
    },
    fetchAccountRequests(customer_id, params) {
      return new Promise((resolve, reject) => {
        let url = `v2/customer_requests/load_by_customer?customer_id=${customer_id}`;
        if (params) {
          url = `${url}&${params.join("&")}`;
        }
        axios
          .get(url)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            console.log(error);
            this.$notify.error({
              title: "PLC Requests Error",
              message: "An error occurred while getting PLC Requests: " + error,
            });
            reject(error);
          });
      });
    },

    clearParams() {
      this.isFiltered = false;
      this.q = {
        uuid: "",
        device_id: "",
        state: "",
      };
      this.loadRequests();
    },
  },
};
</script>

<style lang="scss">
.requests table td .cell .pending,
.requests table td .cell .pending_stock {
  color: #e6a23c;
}
.requests table td .cell .on_hold,
.requests table td .cell .internal_on_hold,
.requests table td .cell .external_on_hold,
.requests table td .cell .label_created {
  color: #909399;
}

.requests table td .cell .shipped,
.requests table td .cell .in_process {
  color: #1998d5;
}

.requests table td .cell .resolved,
.requests table td .cell .paid {
  color: #67c23a;
}
.requests table td .cell .manual_tracking,
.requests table td .cell .waiting_for_updates {
  color: orange;
}

.requests table td .cell .canceled,
.requests table td .cell .failed {
  color: #f56c6c;
}

.el-table__expanded-cell {
  padding: 0px !important;
}
.el-tabs__item {
  font-size: 1.2em;
  margin: 8px 0px;
}
</style>
